import type {
  AnalyticsConfig,
  AnalyticsService,
} from "$src/core/services/analytics/analytics";
import Logging from "$src/core/services/logging/logging";

declare global {
  interface Window {
    __gaMeasurementId: string;
    dataLayer: any;
    gtag: any;
  }
}

const Analytics: AnalyticsService<Extract<AnalyticsConfig, { type: "ga4" }>> = {
  init: ({ measurementId }) => {
    if (typeof window === "undefined" || __DEV__) return;

    if (!measurementId) {
      return;
    }

    if (!window.gtag) {
      // Standard GA stub: queue events in dataLayer before the script loads.
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
      window.__gaMeasurementId = measurementId;

      window.gtag("js", new Date());
      window.gtag("config", measurementId, {
        page_title: document.title,
        page_location: window.location.href,
        debug_mode: false,
      });

      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

      script.onerror = () => {
        Logging.error("Failed to load gtag script");
      };

      document.head.appendChild(script);
    }
  },

  setUserProperty: (name, value) => {
    if (typeof window === "undefined" || __DEV__) return;

    if (!window.gtag || !window.__gaMeasurementId) return;

    window.gtag("config", window.__gaMeasurementId, {
      [name]: value,
    });
  },

  trackEvent: (event) => {
    if (typeof window === "undefined" || __DEV__) return;

    if (!window.gtag) return;

    try {
      const { name, ...params } = event;
      window.gtag("event", name, params);
    } catch (error) {
      Logging.error("Failed to send event to GA", { error });
    }
  },
};

export default Analytics;
